import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { ticketApiClient } from '../services/ticketApiClient';

interface SupportMetrics {
    needsAttention: number;
    inProgress: number;
    notStarted: number;
}

interface SupportContextType {
    metrics: SupportMetrics;
    accountTeam: any[];
    loading: boolean;
    refreshMetrics: (scope?: 'my' | 'org', existingData?: SupportMetrics) => Promise<void>;
    currentScope: 'my' | 'org';
}

const defaultMetrics: SupportMetrics = {
    needsAttention: 0,
    inProgress: 0,
    notStarted: 0,
};


const defaultTeamMembers = [

];

const SupportContext = createContext<SupportContextType | undefined>(undefined);

export const SupportProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [metrics, setMetrics] = useState<SupportMetrics>(defaultMetrics);
    const [accountTeam, setAccountTeam] = useState<any[]>(defaultTeamMembers);
    const [loading, setLoading] = useState(false);
    const [currentScope, setCurrentScope] = useState<'my' | 'org'>('my');

    const refreshMetrics = useCallback(async (scope: 'my' | 'org' = currentScope, existingData?: SupportMetrics) => {
        try {
            setLoading(true);
            setCurrentScope(scope);

            // 1. Update metrics from existing data or fetch new ones
            if (existingData) {
                setMetrics(existingData);
            } else {
                const data = await ticketApiClient.getTickets(undefined, scope);
                if (data.summary) {
                    setMetrics(data.summary);
                }
            }

            // 2. Fetch Account Team only if not already loaded
            if (accountTeam.length === 0) {
                try {
                    const { profileApiClient } = await import('../services/profileApiClient');
                    const dashboardRes = await profileApiClient.getDashboard();
                    if (dashboardRes.accountTeam && dashboardRes.accountTeam.length > 0) {
                        setAccountTeam(dashboardRes.accountTeam);
                    }
                } catch (teamErr) {
                    console.error('[SupportContext] Failed to fetch account team:', teamErr);
                }
            }
        } catch (err) {
            console.error('[SupportContext] Failed to fetch metrics:', err);
        } finally {
            setLoading(false);
        }
    }, [currentScope, accountTeam.length]);

    // Initial fetch
    useEffect(() => {
        refreshMetrics('my');
    }, []);

    return (
        <SupportContext.Provider value={{ metrics, accountTeam, loading, refreshMetrics, currentScope }}>
            {children}
        </SupportContext.Provider>
    );
};

export const useSupport = () => {
    const context = useContext(SupportContext);
    if (context === undefined) {
        throw new Error('useSupport must be used within a SupportProvider');
    }
    return context;
};
